import React, { useContext } from 'react';
import { Stack, Typography, Card, CardContent, CardHeader, Button, Skeleton } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { neutral } from '_lex/_foundations/phxColors';
import { Edit as EditIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { DTBLibraryContext } from '../../context';
import type { ContentReference } from '../../../_types/dtb';
import { usePolicyReferenceResolver } from '../../_utils/policyReferenceUtils';

interface PolicyReferencesProps {
  contentReferences?: ContentReference[];
}

const PolicyReferences: React.FC<PolicyReferencesProps> = ({ contentReferences }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { handleSave, editedData } = useContext(DTBLibraryContext);
  const { resolvedRefs, isLoading } = usePolicyReferenceResolver(contentReferences ?? []);

  const handleEditClick = async () => {
    await handleSave(false);
    navigate(`/admin/dtbs/library/${id}/content-reference`);
  };

  return (
    <Card className='lex-drawer-card'>
      <CardHeader
        title={t('dtbs.library.detail.contentReferences.title')}
        action={
          <Button
            variant='outlined'
            color='neutral'
            size='small'
            className='xs icon-only'
            startIcon={<EditIcon />}
            onClick={handleEditClick}
            disabled={!editedData?.manual?.id}
          />
        }
      />
      <CardContent>
        {isLoading ? (
          <Stack spacing={1}>
            <Skeleton variant='text' width='80%' />
            <Skeleton variant='text' width='60%' />
          </Stack>
        ) : resolvedRefs.length > 0 ? (
          <Stack spacing={1}>
            {resolvedRefs.map((ref, index) => (
              <Typography
                key={`${ref.name}-${index}`}
                variant='bodyMd'
                sx={{ minWidth: 0, whiteSpace: 'normal' }}
              >
                {ref.name}
              </Typography>
            ))}
          </Stack>
        ) : (
          <Typography
            variant='bodyMd'
            color={neutral[400]}
            sx={{ minWidth: 0, whiteSpace: 'normal' }}
          >
            {!editedData?.manual?.id
              ? t('dtbs.library.detail.contentReferences.noManualEmpty')
              : t('dtbs.library.detail.contentReferences.empty')}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default PolicyReferences;
